import React, { useContext } from "react";
import { SDLCMetadata } from "@sscs/types";
import { DataTable, Page, hasActiveFilters } from "@sscs/ui";
import { vulnerabilitiesID } from "../../constants";
import { selectScrollTo } from "../../store/sdlcReportSlice";
import { filterUpdated, initFilter, initPagination, initSort, paginationUpdated, selectFilter, selectPagination, selectSort, severityFiltered, sortUpdated, tagFiltered, } from "../../store/vulnerabilitySlice";
import { CountBadge, ExportButton } from "../common";
import { ReportContext } from "../context";
import { useReportDispatch, useReportSelector, useScrollIntoView, } from "../hooks";
import { useGetReportSummaryQuery } from "../services/shared.api";
import { useGetVulnerabilitiesQuery } from "../services/vulnerabilities.api";
import { getQueryStatus } from "../utils";
import Vulnerability from "./Vulnerability/Vulnerability";
import Filter from "./VulnerabilityFilter/VulnerabilityFilter";
const COLUMNS = [
    { id: "info", label: "Info", width: "70px" },
    {
        id: "cvss",
        label: "CVSS score",
        sortProperty: "cvss",
        sortId: "bom-vulnerabilities-sort-cvss-score",
        startReverse: true,
        width: "135px",
    },
    {
        id: "cve",
        label: "CVE identifier",
        sortProperty: "cve",
        sortId: "bom-vulnerabilities-sort-cve",
        startReverse: true,
        width: "180px",
    },
    { id: "summary", label: "Summary", width: "100%" },
    { id: "tags", label: "Tags", width: "300px" },
];
const Vulnerabilities = () => {
    var _a, _b;
    const { reportId, diffId, roles, onReportExport, activeExport, selectedCheck, } = useContext(ReportContext);
    const dispatch = useReportDispatch();
    const scrollTo = useReportSelector(selectScrollTo);
    const filter = useReportSelector(selectFilter);
    const sort = useReportSelector(selectSort);
    const pagination = useReportSelector(selectPagination);
    const { data: reportSummaryData } = useGetReportSummaryQuery({
        diff_report_id: diffId,
        ui_id: reportId,
    });
    const { data: fetchedData, isLoading, isFetching, isError, error, } = useGetVulnerabilitiesQuery({ filter, pagination, sort, ui_id: reportId });
    // Triaged should not count towards total count
    const statistics = (_b = (_a = reportSummaryData === null || reportSummaryData === void 0 ? void 0 : reportSummaryData.checks_report.report.scans[selectedCheck]) === null || _a === void 0 ? void 0 : _a.statistics.vulnerabilities) !== null && _b !== void 0 ? _b : Array(SDLCMetadata.vulnerability_metrics_t.metrics_cnt_k).fill(0);
    const triagedCount = statistics[SDLCMetadata.vulnerability_metrics_t.vuln_triaged_k];
    const activeCount = statistics[SDLCMetadata.vulnerability_metrics_t.vuln_total_k] -
        triagedCount;
    const data = fetchedData
        ? fetchedData
        : {
            vulnerabilities: [],
            match_count: 0,
            next_page: "",
            previous_page: "",
        };
    useScrollIntoView(scrollTo, vulnerabilitiesID);
    return (React.createElement(Page.Root, { id: vulnerabilitiesID },
        React.createElement(Page.Header, { title: "Detected vulnerabilities", details: [
                `Total: ${activeCount.toLocaleString()} Active`,
                triagedCount > 0 && `${triagedCount.toLocaleString()} Triaged`,
                React.createElement(CountBadge, { key: "critical", count: statistics[SDLCMetadata.vulnerability_metrics_t.vuln_critical_k], label: "Critical", 
                    // CVE severity is defined by NVD: https://nvd.nist.gov/vuln-metrics/cvss
                    severity: "critical", onClick: () => dispatch(severityFiltered("critical")) }),
                React.createElement(CountBadge, { key: "high", count: statistics[SDLCMetadata.vulnerability_metrics_t.vuln_high_k], label: "High", severity: "high", onClick: () => dispatch(severityFiltered("high")) }),
                React.createElement(CountBadge, { key: "medium", count: statistics[SDLCMetadata.vulnerability_metrics_t.vuln_medium_k], label: "Medium", severity: "medium", onClick: () => dispatch(severityFiltered("medium")) }),
                React.createElement(CountBadge, { key: "low", count: statistics[SDLCMetadata.vulnerability_metrics_t.vuln_low_k], label: "Low", severity: "low", onClick: () => dispatch(severityFiltered("low")) }),
                React.createElement(CountBadge, { key: "fixable", count: statistics[SDLCMetadata.vulnerability_metrics_t.vuln_fixable_k], label: "Fix available", severity: "none", onClick: () => dispatch(tagFiltered(["FIXABLE"])) }),
            ], actions: roles.canExportCve ? (React.createElement(ExportButton, { id: "detected-vulnerabilities-export-as-cve", text: "Export as CSV", onClick: () => onReportExport === null || onReportExport === void 0 ? void 0 : onReportExport("cve"), isDisabled: activeExport.cve })) : undefined }),
        React.createElement(Page.Filters, { found: {
                id: "bom-vulnerabilities-clear-all-filters",
                count: data.match_count,
                initialFilters: initFilter,
                activeFilters: filter,
                onClear: () => dispatch(filterUpdated(initFilter)),
            } },
            React.createElement(Filter, null)),
        React.createElement(Page.Body, null,
            React.createElement(DataTable
            // Force rerender of the table to collapse expanded rows when filter/pagination/sort changes
            , { 
                // Force rerender of the table to collapse expanded rows when filter/pagination/sort changes
                key: `${JSON.stringify(filter)} ${JSON.stringify(sort)} ${JSON.stringify(pagination)}`, columns: COLUMNS, sort: sort, onSortChange: (sort) => dispatch(sortUpdated(sort)), empty: {
                    subject: "vulnerabilities",
                    filters: {
                        active: hasActiveFilters(initFilter, filter),
                        onReset: () => dispatch(filterUpdated(initFilter)),
                    },
                }, status: getQueryStatus({
                    isLoading,
                    isFetching,
                    isError,
                    error,
                    data: fetchedData,
                }), pagination: {
                    id: "bom-vulnerabilities-pagination",
                    itemCount: data.match_count,
                    pagination,
                    onChange: (pagination) => dispatch(paginationUpdated(pagination)),
                } }, data.vulnerabilities.map((vulnerability, index) => (React.createElement(Vulnerability, { rowIndex: index, key: vulnerability.cve_number, vulnerability: vulnerability })))))));
};
export default Vulnerabilities;
export { initFilter, initPagination, initSort };
//# sourceMappingURL=Vulnerabilities.js.map